/**
 * Dialog for choosing how to apply updates to a shift series
 * Provides options: this occurrence only, this and future, or entire series
 */

import { useLingui } from '@lingui/react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { shifts_UpdateShiftSeriesRequest } from '@/api/models/shifts_UpdateShiftSeriesRequest';

/**
 * #1156: the generated enum rather than a hand-written literal union. Every
 * member value is identical ('this_only' | 'this_and_future' |
 * 'entire_series'), so nothing changes at runtime — but the update request DTO
 * now takes the value directly (TypeScript enums are nominal).
 */
export type UpdateScope = shifts_UpdateShiftSeriesRequest.update_scope;

export interface SeriesUpdateChoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChoose: (scope: UpdateScope) => Promise<void>;
  isPending: boolean;
  /**
   * Whether the recurrence changed. Only the entire series can take a new recurrence (D1,
   * #2119), so the dialog then offers that scope alone and says why.
   */
  hasRecurrenceChanges?: boolean;
  /** Whether the status changed: the dialog says it applies to every shift of the scope. */
  hasStatusChange?: boolean;
}

export function SeriesUpdateChoiceDialog({
  open,
  onOpenChange,
  onChoose,
  isPending,
  hasRecurrenceChanges = false,
  hasStatusChange = false,
}: SeriesUpdateChoiceDialogProps) {
  const { i18n } = useLingui();

  const handleChoose = async (scope: UpdateScope) => {
    await onChoose(scope);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {i18n._({
              id: 'shifts.series.updateChoice.title',
              message: 'Update Series',
            })}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              {hasRecurrenceChanges ? (
                <p data-testid="recurrence-only-entire">
                  {i18n._({
                    id: 'shifts.series.updateChoice.recurrenceEntireOnly',
                    message:
                      'You changed the repetitions or the pattern. That always applies to the whole series: new days are added as drafts, days that drop out are removed, and existing shifts keep their status and confirmation. Days on which the employee already works or is absent are skipped.',
                  })}
                </p>
              ) : (
                <p>
                  {i18n._({
                    id: 'shifts.series.updateChoice.description',
                    message: 'How would you like to apply these changes?',
                  })}
                </p>
              )}
              {hasStatusChange && (
                <p data-testid="status-scope-note">
                  {i18n._({
                    id: 'shifts.series.updateChoice.statusNote',
                    message:
                      'The new status applies to every shift of the part you choose. Publishing notifies the employee once.',
                  })}
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          {/* This occurrence only: not with a recurrence change (D1, #2119) */}
          {!hasRecurrenceChanges && (
            <Button
              variant="outline"
              className="h-auto w-full justify-start whitespace-normal py-2 text-left"
              onClick={() => handleChoose(shifts_UpdateShiftSeriesRequest.update_scope.THIS_ONLY)}
              disabled={isPending}
              data-testid="update-this-only-button"
            >
              {isPending && (
                <Loader2
                  className="mr-2 h-4 w-4 animate-spin"
                  aria-hidden="true"
                />
              )}
              <div className="flex flex-col items-start">
                <span className="font-medium">
                  {i18n._({
                    id: 'shifts.series.updateChoice.thisOnly',
                    message: 'This occurrence only',
                  })}
                </span>
                <span className="text-xs text-muted-foreground font-normal">
                  {i18n._({
                    id: 'shifts.series.updateChoice.thisOnlyDesc',
                    message: 'Mark this shift as individually modified',
                  })}
                </span>
              </div>
            </Button>
          )}

          {/* This and future shifts: not with a recurrence change (D1, #2119) */}
          {!hasRecurrenceChanges && (
            <Button
              variant="outline"
              className="h-auto w-full justify-start whitespace-normal py-2 text-left"
              onClick={() => handleChoose(shifts_UpdateShiftSeriesRequest.update_scope.THIS_AND_FUTURE)}
              disabled={isPending}
              data-testid="update-this-future-button"
            >
              {isPending && (
                <Loader2
                  className="mr-2 h-4 w-4 animate-spin"
                  aria-hidden="true"
                />
              )}
              <div className="flex flex-col items-start">
                <span className="font-medium">
                  {i18n._({
                    id: 'shifts.series.updateChoice.thisFuture',
                    message: 'This and future shifts',
                  })}
                </span>
                <span className="text-xs text-muted-foreground font-normal">
                  {i18n._({
                    id: 'shifts.series.updateChoice.thisFutureDesc',
                    message: 'Update shifts from this date onwards',
                  })}
                </span>
              </div>
            </Button>
          )}

          {/* Entire series */}
          <Button
            variant="default"
            className="h-auto w-full justify-start whitespace-normal py-2 text-left"
            onClick={() => handleChoose(shifts_UpdateShiftSeriesRequest.update_scope.ENTIRE_SERIES)}
            disabled={isPending}
            data-testid="update-entire-series-button"
          >
            {isPending && (
              <Loader2
                className="mr-2 h-4 w-4 animate-spin"
                aria-hidden="true"
              />
            )}
            <div className="flex flex-col items-start">
              <span className="font-medium">
                {i18n._({
                  id: 'shifts.series.updateChoice.entireSeries',
                  message: 'Entire series',
                })}
              </span>
              <span className="text-xs text-muted-foreground font-normal">
                {hasRecurrenceChanges
                  ? i18n._({
                      id: 'shifts.series.updateChoice.entireSeriesDescDelta',
                      message: 'Apply the new recurrence to the whole series',
                    })
                  : i18n._({
                      id: 'shifts.series.updateChoice.entireSeriesDescAll',
                      message: 'All shifts of the series except the individually modified ones',
                    })}
              </span>
            </div>
          </Button>

          <AlertDialogCancel
            className="mt-0 w-full"
            data-testid="cancel-button"
          >
            {i18n._({ id: 'common.cancel', message: 'Cancel' })}
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
